/*
 * Copyright (c) 2021 LucidResolutions LLC. All rights reserved.
 */

import {RBTree} from "bintrees";
import BigNumber from "bignumber.js";


const askComparator = (a: Node,b: Node)=>{
    return a.price - b.price
}

const bidComparator = (a: Node,b: Node)=>{
    return b.price - a.price
}


class Node {
    price: number;
    amount: number;
    isBid: boolean = true

    constructor(price: number, amount: number, isBid: boolean = true) {
        this.price = price
        this.amount = amount;
        this.isBid = isBid
    }

    compare(a: Node,b: Node){
        if(this.isBid){
            return bidComparator(a,b)
        }

        return askComparator(a,b)
    }
}

export class AskNode extends Node {
    constructor(price: number, amount: number) {
        super(price, amount, false);
    }
}

export class BidNode extends Node {
    constructor(price: number, amount: number) {
        super(price, amount, true);
    }
}

export enum OrderBookSide {
    Bid = 'bid',
    Ask = 'ask'
}


export class OrderBook {
    askTree: RBTree<AskNode>;
    bidTree: RBTree<BidNode>;
    size: number;

    constructor(size: number = 0, bids: BidNode[] = [], asks: AskNode[] = []) {
        this.size = size
        this.askTree = new RBTree<AskNode>(askComparator)
        this.bidTree = new RBTree<AskNode>(bidComparator)
        asks.forEach(this.addAsk.bind(this))
        bids.forEach(this.addBid.bind(this))
    }

    ticker() {
        return {
            bid: new BigNumber(this.bidTree.min()?.price || 0),
            ask: new BigNumber(this.askTree.min()?.price || 0)
        }
    }

    add(side: OrderBookSide, price: number, amount: number){
        if(side == 'bid'){
            this.addBid(new BidNode(price, amount))
        } else {
            this.addAsk(new AskNode(price, amount))
        }
    }

    remove(side: OrderBookSide, price: number){
        if(side == 'bid'){
            this.removeBid(new BidNode(price, 0))
        } else {
            this.removeAsk(new AskNode(price, 0))
        }
    }

    addAsk(ask: AskNode){
        this.askTree.insert(ask)
        while(this.size && this.askTree.size > this.size){
            this.removeAsk(this.askTree.max())
        }
    }

    removeAsk(ask: AskNode | null) {
        if(!ask){return}
        this.askTree.remove(ask)
    }

    addBid(bid: BidNode){
        this.bidTree.insert(bid)
        while(this.size && this.bidTree.size > this.size){
            this.removeBid(this.bidTree.max())
        }
    }

    removeBid(bid: BidNode | null) {
        if(!bid){return}
        this.bidTree.remove(bid)
    }
}
